import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../redux/Slice/authSlice";
import { errorMessage } from "../services";
import { chooseAccount, forgotPassword, login, resetPassword, sendLoginOtp, verifyLoginOtp } from "../services/auth";
import { clock, useResendTimer } from "../utils/hooks";
import { askOnSignIn } from "../utils/pushNotifications";
import "./admin.css";
import { Spinner } from "../components/Loader";
import OwnezzyLogo from "../components/OwnezzyLogo";
import PasswordInput from "../components/PasswordInput";

// Sign in with email, mobile number or username and a password, or with a one-time code emailed to the account.
// "Forgot password" sends a one-time code, then asks for a new password. A code can be sent again after 30 seconds.
// A partner and a store owner may share an email or mobile: when the sign-in fits both, the person picks one.
export default function Login() {
  const dispatch = useDispatch();
  const [mode, setMode] = useState("login"); // login | otp (enter email) | otp-code | forgot | reset | choose
  const [form, setForm] = useState({ login: "", password: "", code: "", email: "", otp: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [choice, setChoice] = useState(null); // { accounts: [{ id, label, name }], token } after a sign-in fitting two
  const resend = useResendTimer(30);

  // A sign-in answer: a session, or the accounts to pick from
  const signedIn = (data) => {
    if (data.chooseAccount) {
      setChoice({ accounts: data.chooseAccount, token: data.choiceToken });
      setMode("choose");
      return;
    }
    dispatch(loginSuccess({ token: data.token, user: data.user }));
  };
  const pick = async (account) => {
    askOnSignIn();
    setError("");
    setBusy(true);
    try {
      const { data } = await chooseAccount(choice.token, account.id);
      dispatch(loginSuccess({ token: data.token, user: data.user }));
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const go = (next) => {
    setMode(next);
    setError("");
    setInfo("");
  };

  const submit = async (e) => {
    e.preventDefault();
    // Order alerts in other tabs and with the panel closed need notifications: asked here, while this is a click
    if (["login", "otp", "otp-code"].includes(mode)) askOnSignIn();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await login({ login: form.login.trim(), password: form.password });
        signedIn(data);
      } else if (mode === "otp") {
        const { data } = await sendLoginOtp(form.email.trim());
        // Local development only: the server returns the code when no mailbox is connected
        setInfo(data.devOtp ? `${data.message} (Development code: ${data.devOtp})` : data.message);
        resend.start();
        setMode("otp-code");
      } else if (mode === "otp-code") {
        const { data } = await verifyLoginOtp(form.email.trim(), form.otp.trim());
        signedIn(data);
      } else if (mode === "forgot") {
        const { data } = await forgotPassword(form.login.trim());
        setInfo(data.message);
        resend.start();
        setMode("reset");
      } else {
        const { data } = await resetPassword({ login: form.login.trim(), code: form.code.trim(), password: form.password });
        setForm((f) => ({ ...f, password: "", code: "" }));
        setMode("login");
        setInfo(data.message);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // The login code (otp-code) or the reset code (reset) again, once the 30-second wait is over
  const resendCode = async () => {
    setError("");
    setInfo("");
    try {
      const { data } = mode === "reset" ? await forgotPassword(form.login.trim()) : await sendLoginOtp(form.email.trim());
      setInfo(data.devOtp ? `${data.message} (Development code: ${data.devOtp})` : data.message);
      resend.start();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const resendButton = (
    <button type="button" className="text-btn" onClick={resendCode} disabled={resend.left > 0}>
      {resend.left > 0 ? `Resend code in ${clock(resend.left)}` : "Resend code"}
    </button>
  );

  const signingIn = ["login", "otp", "otp-code"].includes(mode);
  const titles = { login: "Sign in", otp: "Sign in", "otp-code": "Enter your login code", forgot: "Reset your password", reset: "Enter your reset code", choose: "Choose an account" };
  const intro = {
    login: "Use the email, mobile number or username of your Ownezzy account.",
    otp: "Enter the email on your account. We will email you a 6-digit login code.",
    "otp-code": `Enter the 6-digit code we emailed to ${form.email.trim()}.`,
    forgot: "We will send a one-time code to the phone number on your account.",
    reset: "Enter the code you received and choose a new password.",
    choose: "This email or mobile is on two Ownezzy accounts. Which one do you want to open?",
  };

  if (mode === "choose" && choice) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <div className="login-logo"><OwnezzyLogo wide /></div>
          <h1>{titles.choose}</h1>
          <p>{intro.choose}</p>
          {error && <div className="form-error">{error}</div>}
          <div className="login-accounts">
            {choice.accounts.map((a) => (
              <button key={a.id} type="button" className="login-account" disabled={busy} onClick={() => pick(a)}>
                <span className="login-account-icon" aria-hidden="true">{a.role === "subadmin" ? "🤝" : "🏪"}</span>
                <span><b>{a.label}</b><small>{a.name}</small></span>
                <span aria-hidden="true">→</span>
              </button>
            ))}
          </div>
          <button type="button" className="text-btn" onClick={() => { setChoice(null); go("login"); }}>Back to sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={submit}>
        <div className="login-logo"><OwnezzyLogo wide /></div>
        <h1>{titles[mode]}</h1>
        <p>{intro[mode]}</p>

        {signingIn && (
          <div className="login-method" role="tablist" aria-label="How to sign in">
            <button type="button" role="tab" aria-selected={mode === "login"} className={mode === "login" ? "active" : ""} onClick={() => go("login")}>🔑 Password</button>
            <button type="button" role="tab" aria-selected={mode !== "login"} className={mode !== "login" ? "active" : ""} onClick={() => go("otp")}>
              ✉️ Email OTP
            </button>
          </div>
        )}
        {error && <div className="form-error">{error}</div>}
        {info && <div className="form-ok">{info}</div>}

        {(mode === "otp" || mode === "otp-code") && (
          <label className="admin-field">
            Email
            <input type="email" inputMode="email" autoComplete="email" required autoFocus={mode === "otp"}
              value={form.email} onChange={set("email")} disabled={mode === "otp-code"} placeholder="you@example.com" />
          </label>
        )}
        {mode === "otp-code" && (
          <label className="admin-field">
            Login code
            <input className="login-otp-input" required autoFocus autoComplete="one-time-code" inputMode="numeric" pattern="\d{6}" maxLength={6}
              value={form.otp} onChange={set("otp")} placeholder="••••••" />
          </label>
        )}

        {!["otp", "otp-code"].includes(mode) && (
          <label className="admin-field">
            Email, mobile or username
            <input autoComplete="username" required autoFocus value={form.login} onChange={set("login")} />
          </label>
        )}
        {mode === "reset" && (
          <label className="admin-field">
            Reset code
            <input required autoComplete="one-time-code" value={form.code} onChange={set("code")} />
          </label>
        )}
        {(mode === "login" || mode === "reset") && (
          <label className="admin-field">
            {mode === "reset" ? "New password" : "Password"}
            <PasswordInput
              required
              minLength={mode === "reset" ? 8 : undefined}
              autoComplete={mode === "reset" ? "new-password" : "current-password"}
              value={form.password}
              onChange={set("password")}
            />
          </label>
        )}
        <button className="primary-btn" disabled={busy}>
          {busy ? <><Spinner /> Please wait</> : { login: "Sign in", otp: "Email me a code", "otp-code": "Verify & sign in", forgot: "Send reset code", reset: "Set new password" }[mode]}
        </button>
        {mode === "login" && <button type="button" className="text-btn" onClick={() => go("forgot")}>Forgot password?</button>}
        {mode === "otp-code" && (
          <div className="login-otp-links">
            {resendButton}
            <button type="button" className="text-btn" onClick={() => { setForm((f) => ({ ...f, otp: "" })); go("otp"); }}>Change email</button>
          </div>
        )}
        {mode === "reset" && <div className="login-otp-links">{resendButton}</div>}
        {(mode === "forgot" || mode === "reset") && <button type="button" className="text-btn" onClick={() => go("login")}>Back to sign in</button>}
      </form>
    </div>
  );
}
